import {
  generateGetAction,
  generatePostAction,
} from '@services/ActionDispatcher';

import {
  downloadAllGuestAttachmentsAction,
  downloadGuestAttachmentAction,
  getGuestReferCatalogAction,
  getGuestReferLinkAction,
  getGuestReferralAction,
  requestGuestLinkAction,
  resendGuestLinkAction,
  revokeGuestLinkAction,
  submitGuestReferralAction,
  validateGuestReferStepAction,
} from './GuestActionConfig';

/**
 * ENH-052: read one referral using a no-login token.
 *
 * The token is the only credential — there is no session — so every guest call
 * is read/request-only: the referral, its files, and a fresh link.
 */
const getGuestReferralRequest = (token: string) => generateGetAction(
  // action name
  getGuestReferralAction,
  // url
  `guest/referrals/${token}`,
  {},
  {},
);

/** Ask for a fresh link when the current one is expired or revoked. */
const requestGuestLinkRequest = (token: string) => generatePostAction(
  // action name
  requestGuestLinkAction,
  // url
  `guest/referrals/${token}/request-link`,
  {},
);

/**
 * A short-lived download URL for one of the referral's files (client,
 * 2026-09-23: the guest view shows the files, downloadable). Audited server-side.
 */
const downloadGuestAttachmentRequest = (
  token: string,
  attachmentId: string | number,
) => generatePostAction(
  downloadGuestAttachmentAction,
  `guest/referrals/${token}/attachments/${attachmentId}/download`,
  {},
);

/** Every file's URL, for the details panel's "Download all". */
const downloadAllGuestAttachmentsRequest = (token: string) => generatePostAction(
  downloadAllGuestAttachmentsAction,
  `guest/referrals/${token}/attachments/download-all`,
  {},
);

/**
 * Sender-side control of a referral's guest link. Authenticated and
 * ownership-checked on the server: only the clinician who sent the referral
 * may use these.
 */
const resendGuestLinkRequest = (referralId: string) => generatePostAction(
  resendGuestLinkAction,
  `referrals/${referralId}/guest-access/resend`,
  {},
);

const revokeGuestLinkRequest = (referralId: string) => generatePostAction(
  revokeGuestLinkAction,
  `referrals/${referralId}/guest-access/revoke`,
  {},
);

/**
 * ENH-052: a clinic's public referral link. A dentist without an account
 * sends the clinic a referral through it; nothing is written before the
 * final submission.
 */
const getGuestReferLinkRequest = (slug: string) => generateGetAction(
  getGuestReferLinkAction,
  `guest/refer/${slug}`,
);

/** The lists the form picks from (allergies, sedations, considerations, specialties). */
const getGuestReferCatalogRequest = (slug: string) => generateGetAction(
  getGuestReferCatalogAction,
  `guest/refer/${slug}/catalog`,
);

/** Checks one step on Next with the submission's rules; saves nothing. */
const validateGuestReferStepRequest = (
  slug: string,
  step: string,
  data: any,
) => generatePostAction(
  validateGuestReferStepAction,
  `guest/refer/${slug}/validate/${step}`,
  data,
);

/** The whole referral, once. The same submission_key again answers with the first result. */
const submitGuestReferralRequest = (slug: string, data: any) => generatePostAction(
  submitGuestReferralAction,
  `guest/refer/${slug}`,
  data,
);

export {
  getGuestReferralRequest,
  requestGuestLinkRequest,
  downloadGuestAttachmentRequest,
  downloadAllGuestAttachmentsRequest,
  resendGuestLinkRequest,
  revokeGuestLinkRequest,
  getGuestReferLinkRequest,
  getGuestReferCatalogRequest,
  validateGuestReferStepRequest,
  submitGuestReferralRequest,
};
